import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { plural } from '@lingui/core/macro';
import { AuthContext } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useTheme } from '../contexts/ThemeContext';
import { accountService } from '../services/accountService';
import { agentService, AgentExecution } from '../services/agentService';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Layout from '../components/Layout';
import { clientDisplayName, surfaceColor } from '../constants/clientSurfaces';

interface AuditLog {
  id: string;
  continuationId?: string;
  /** The AI provider the traffic went to. Rendered from aiProviders.ts. */
  platform: string;
  /**
   * The PrivacyPal surface that protected it: desktop app, iPhone, Android.
   * A different question from `platform`. Null on rows written before the
   * dimension existed, which render as "Unattributed" rather than being
   * guessed into a device the user may never have owned.
   */
  client?: string | null;
  deviceId?: string | null;
  prompt: string;
  timestamp: string;
  hasAttachment: boolean;
  riskLevel?: string;
  agentExecutions?: AgentExecution[];
  fullPrompt?: string;
  encodedPrompt?: string;
  response?: string;
  metadata?: {
    model?: string;
    tokensIn?: number;
    tokensOut?: number;
    promptCount?: number;
  };
  attachments?: Array<{
    name: string;
    size: string;
    type: string;
    encodedContent?: string;
  }>;
  entityTypes?: string[];
  peaqTransactionHash?: string;
  type?: string;
  fileUploads?: Array<{
    id: string;
    continuationId: string;
    fileName: string;
    fileSize: string;
    fileType: string;
    timestamp: string;
    entityTypes: string[];
    riskLevel: string;
  }>;
  sessionPrompts?: Array<{
    original: string;
    encoded: string;
    timestamp: string;
    entityTypes: string[];
    files: string[];
    model: string;
    tokensIn: number;
    tokensOut: number;
  }>;
  isUnlinked?: boolean;
}

interface UserAuditData {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    status: string;
    effectiveStatus?: string;
    tier?: 'pro' | 'max';
    effectiveTier?: 'pro' | 'max';
    totalPrompts: number;
    monthlyPrompts: number;
  };
  usage: Array<{
    date: string;
    chatgpt: number;
    claude: number;
    copilot: number;
  }>;
  auditLogs: AuditLog[];
  stats: {
    totalPrompts: number;
    platforms: string[];
    filesShared: {
      pdf: number;
      csv: number;
    };
  };
}

// Provider keys of the /api/company/users/:id/usage day series (the
// ai-platforms registry rollup on the API), with a display color each.
// 'other' is the API's catch-all bucket for unresolvable platform ids.
const USAGE_PROVIDER_SERIES: { key: string; label: string; color: string }[] = [
  { key: 'chatgpt', label: 'ChatGPT', color: '#111111' },
  { key: 'claude', label: 'Claude', color: '#6366f1' },
  { key: 'gemini', label: 'Gemini', color: '#4285f4' },
  { key: 'copilot', label: 'Copilot', color: '#0078d4' },
  { key: 'grok', label: 'Grok', color: '#8b5cf6' },
  { key: 'perplexity', label: 'Perplexity', color: '#14b8a6' },
  { key: 'hermes', label: 'Hermes', color: '#f59e0b' },
  { key: 'other', label: 'Other AI', color: '#9ca3af' },
];

export default function UserDetailAuditPage() {
  const { t } = useLingui();
  const { userId } = useParams<{ userId: string }>();
  const { user: currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [auditData, setAuditData] = useState<UserAuditData | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAudit, setSelectedAudit] = useState<AuditLog | null>(null);
  const [_expandedFiles, setExpandedFiles] = useState<Set<number>>(new Set());
  const [expandedEncodedPrompt, setExpandedEncodedPrompt] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  /** Default 365 days so migrated Aura history and older sessions remain visible (30d often showed empty). */
  const [auditWindowDays, setAuditWindowDays] = useState(365);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    if (userId) {
      loadAuditData(auditWindowDays);
    }
  }, [userId, currentUser, navigate, auditWindowDays]);

  const loadAuditData = async (days: number = 365) => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const userAccount = await accountService.getUserAccountForAdmin(userId);
      const usage = await accountService.getUserUsageForAdmin(userId, days);
      const auditLogs = await accountService.getUserAuditLogsForAdmin(userId, days);

      // Enrich audit logs with agent execution data (non-blocking)
      try {
        const continuationIds = [...new Set(auditLogs.map((l: AuditLog) => l.continuationId).filter(Boolean))] as string[];
        // Fetch in small batches: a 50-wide Promise.allSettled fan-out meant 50
        // concurrent graph queries server-side, which exhausted Neptune's query
        // memory and 500'd the whole page (2026-08-19 incident).
        const idsToEnrich = continuationIds.slice(0, 50);
        const CHUNK = 5;
        const agentResults: PromiseSettledResult<Awaited<ReturnType<typeof agentService.getAgentExecutionsForAudit>>>[] = [];
        for (let i = 0; i < idsToEnrich.length; i += CHUNK) {
          agentResults.push(...await Promise.allSettled(
            idsToEnrich.slice(i, i + CHUNK).map((cid) => agentService.getAgentExecutionsForAudit(cid))
          ));
        }
        const agentMap = new Map<string, AgentExecution[]>();
        agentResults.forEach((r, i) => {
          if (r.status === 'fulfilled' && r.value.executions.length > 0) {
            agentMap.set(continuationIds[i]!, r.value.executions);
          }
        });
        for (const log of auditLogs) {
          if (log.continuationId && agentMap.has(log.continuationId)) {
            log.agentExecutions = agentMap.get(log.continuationId);
          }
        }
      } catch { /* agent enrichment is non-critical */ }

      const platforms = [...new Set(auditLogs.map((log: AuditLog) => log.platform))] as string[];
      
      const filesByContinuationId = new Map<string, Set<string>>();
      for (const log of auditLogs) {
        const continuationId = log.continuationId || log.id;
        if (!continuationId) continue;
        if (!filesByContinuationId.has(continuationId)) {
          filesByContinuationId.set(continuationId, new Set<string>());
        }
        const filesSet = filesByContinuationId.get(continuationId)!;
        if (log.attachments && Array.isArray(log.attachments) && log.attachments.length > 0) {
          for (const attachment of log.attachments) {
            const fileName = (attachment.name || '').toLowerCase().trim();
            if (fileName && fileName.length > 0) {
              filesSet.add(fileName);
            }
          }
        }
      }
      
      let pdfCount = 0;
      let csvCount = 0;
      for (const filesSet of filesByContinuationId.values()) {
        if (filesSet.size === 0) continue;
        for (const fileName of filesSet) {
          if (fileName.endsWith('.pdf')) pdfCount++;
          else if (fileName.endsWith('.csv') || fileName.endsWith('.txt')) csvCount++;
        }
      }
      
      const convertNeo4jInt = (value: any): number => {
        if (value && typeof value === 'object' && 'low' in value) {
          return value.high ? (value.high * 0x100000000) + (value.low >>> 0) : value.low;
        }
        return typeof value === 'number' ? value : parseInt(value) || 0;
      };

      setAuditData({
        user: userAccount,
        usage,
        auditLogs,
        stats: {
          totalPrompts: convertNeo4jInt(userAccount.totalPrompts),
          platforms,
          filesShared: { pdf: pdfCount, csv: csvCount },
        },
      });
    } catch (error: any) {
      console.error('Failed to load audit data:', error);
      showToast(error.response?.data?.error || t`Failed to load user audit data`, 'error');
      navigate('/users');
    } finally {
      setLoading(false);
    }
  };

  const filteredAuditLogs = auditData?.auditLogs.filter((log: AuditLog) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      (log.prompt && log.prompt.toLowerCase().includes(query)) ||
      (log.fullPrompt && log.fullPrompt.toLowerCase().includes(query)) ||
      (log.encodedPrompt && log.encodedPrompt.toLowerCase().includes(query)) ||
      (log.platform && log.platform.toLowerCase().includes(query)) ||
      (log.timestamp && log.timestamp.toLowerCase().includes(query)) ||
      (log.entityTypes && log.entityTypes.some((et: string) => et.toLowerCase().includes(query))) ||
      (log.attachments && log.attachments.some((att: { name: string }) => att.name.toLowerCase().includes(query))) ||
      (log.fileUploads && log.fileUploads.some((f: any) => f.fileName?.toLowerCase().includes(query)))
    );
  }) || [];

  const groupedLogs = React.useMemo(() => {
    const groups = new Map<string, AuditLog[]>();
    const logsWithoutContinuationId: AuditLog[] = [];
    
    for (const log of filteredAuditLogs) {
      if (log.continuationId && log.continuationId.trim() !== '') {
        const continuationId = log.continuationId;
        if (!groups.has(continuationId)) {
          groups.set(continuationId, []);
        }
        groups.get(continuationId)!.push(log);
      } else {
        logsWithoutContinuationId.push(log);
      }
    }
    
    for (const log of logsWithoutContinuationId) {
      groups.set(log.id, [log]);
    }
    
    const allLogs = Array.from(groups.values()).flat();
    const hasUniqueContinuationIds = allLogs.length === groups.size;
    
    if (!hasUniqueContinuationIds) {
      const timeBasedGroups = new Map<string, AuditLog[]>();
      const processedLogIds = new Set<string>();
      
      allLogs.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      
      for (let i = 0; i < allLogs.length; i++) {
        const log1 = allLogs[i];
        if (processedLogIds.has(log1.id)) continue;
        
        const timeGroup: AuditLog[] = [log1];
        const log1Time = new Date(log1.timestamp).getTime();
        const log1Platform = log1.platform;
        
        for (let j = i + 1; j < allLogs.length; j++) {
          const log2 = allLogs[j];
          if (processedLogIds.has(log2.id)) continue;
          if (log2.platform !== log1Platform) continue;
          const timeDiff = Math.abs(log1Time - new Date(log2.timestamp).getTime()) / 1000;
          if (timeDiff <= 300) {
            timeGroup.push(log2);
            processedLogIds.add(log2.id);
          } else {
            break;
          }
        }
        
        if (timeGroup.length > 1) {
          const groupKey = `time-group-${timeGroup[0].id}`;
          timeBasedGroups.set(groupKey, timeGroup);
          processedLogIds.add(log1.id);
          for (let k = 1; k < timeGroup.length; k++) {
            processedLogIds.add(timeGroup[k].id);
          }
        }
      }
      
      if (timeBasedGroups.size > 0) {
        groups.clear();
        for (const [groupKey, timeGroup] of timeBasedGroups.entries()) {
          groups.set(groupKey, timeGroup);
        }
        for (const log of allLogs) {
          if (!processedLogIds.has(log.id)) {
            groups.set(log.continuationId || log.id, [log]);
          }
        }
      }
    }
    
    const result: AuditLog[] = [];
    for (const [continuationId, logs] of groups.entries()) {
      if (logs.length === 1) {
        result.push(logs[0]);
      } else {
        const firstLog = logs[0];
        const allSessionPrompts: Array<{ original: string; encoded: string; timestamp: string; entityTypes: string[]; files: string[]; model: string; tokensIn: number; tokensOut: number; }> = [];
        
        for (const log of logs) {
          if (log.sessionPrompts && Array.isArray(log.sessionPrompts) && log.sessionPrompts.length > 0) {
            allSessionPrompts.push(...log.sessionPrompts);
          } else {
            const files: string[] = [];
            if (log.attachments && Array.isArray(log.attachments)) {
              log.attachments.forEach(att => {
                if (att.name && !files.includes(att.name)) files.push(att.name);
              });
            }
            allSessionPrompts.push({
              original: log.fullPrompt || log.prompt || '',
              encoded: log.encodedPrompt || '',
              timestamp: log.timestamp || '',
              entityTypes: log.entityTypes || [],
              files,
              model: log.metadata?.model || '',
              tokensIn: log.metadata?.tokensIn || 0,
              tokensOut: log.metadata?.tokensOut || 0,
            });
          }
        }
        
        const allFiles = new Map<string, { name: string; size: string; type: string }>();
        for (const log of logs) {
          if (log.attachments && Array.isArray(log.attachments)) {
            for (const att of log.attachments) {
              if (att.name && !allFiles.has(att.name)) {
                allFiles.set(att.name, { name: att.name, size: att.size || 'unknown', type: att.type || att.name.split('.').pop() || 'unknown' });
              }
            }
          }
          if (log.sessionPrompts && Array.isArray(log.sessionPrompts)) {
            for (const prompt of log.sessionPrompts) {
              if (prompt.files && Array.isArray(prompt.files)) {
                for (const fileName of prompt.files) {
                  if (fileName && typeof fileName === 'string' && !allFiles.has(fileName)) {
                    allFiles.set(fileName, { name: fileName, size: 'unknown', type: fileName.split('.').pop() || 'unknown' });
                  }
                }
              }
            }
          }
        }
        
        const allEntityTypes = new Set<string>();
        for (const log of logs) {
          if (log.entityTypes && Array.isArray(log.entityTypes)) {
            log.entityTypes.forEach(et => allEntityTypes.add(et));
          }
        }
        
        const riskLevels = logs.map(l => l.riskLevel || 'LOW');
        const highestRisk = riskLevels.includes('HIGH') ? 'HIGH' : riskLevels.includes('MEDIUM') ? 'MEDIUM' : 'LOW';
        const totalTokensIn = logs.reduce((sum, l) => sum + (l.metadata?.tokensIn || 0), 0);
        const totalTokensOut = logs.reduce((sum, l) => sum + (l.metadata?.tokensOut || 0), 0);
        
        const sortedLogs = [...logs].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
        const earliestLog = sortedLogs[0];
        
        result.push({
          ...firstLog,
          id: earliestLog.id,
          continuationId: continuationId,
          sessionPrompts: allSessionPrompts,
          attachments: Array.from(allFiles.values()),
          entityTypes: Array.from(allEntityTypes),
          riskLevel: highestRisk,
          metadata: { ...firstLog.metadata, tokensIn: totalTokensIn, tokensOut: totalTokensOut, promptCount: allSessionPrompts.length },
          prompt: allSessionPrompts.length > 1 
            ? plural(allSessionPrompts.length, { one: '# prompt in this conversation', other: '# prompts in this conversation' })
            : (allSessionPrompts.length > 0 && (allSessionPrompts[0].encoded || allSessionPrompts[0].original)
                ? ((allSessionPrompts[0].encoded || allSessionPrompts[0].original).length > 100 
                    ? (allSessionPrompts[0].encoded || allSessionPrompts[0].original).substring(0, 100) + '...' 
                    : (allSessionPrompts[0].encoded || allSessionPrompts[0].original))
                : t`No encoded prompt`),
          timestamp: earliestLog.timestamp,
        });
      }
    }
    
    result.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    return result;
  }, [filteredAuditLogs, t]);

  const formatTime = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      if (diffMins < 1) return t`Just now`;
      if (diffMins < 60) return t`${diffMins}m ago`;
      if (diffHours < 24) return t`${diffHours}h ago`;
      if (diffDays === 1) return t`Yesterday`;
      if (diffDays < 7) return t`${diffDays}d ago`;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch { return timestamp; }
  };

  const formatFullTime = (timestamp: string) => {
    try {
      return new Date(timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', timeZoneName: 'short' });
    } catch { return timestamp; }
  };

  const getInitials = (firstName: string, lastName: string) => `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || 'U';

  const getPlatformDisplayName = (platform: string) => {
    const map: Record<string, string> = { chatgpt: 'ChatGPT', claude: 'Claude', 'browser_extension': t`Browser Extension` };
    return map[platform.toLowerCase()] || platform;
  };

  const formatModelName = (model: string | null | undefined, platform: string): string => {
    if (!model) return platform === 'chatgpt' ? 'ChatGPT' : platform === 'claude' ? 'Claude' : platform === 'gemini' ? 'Gemini' : t`unknown`;
    const modelStr = model.toLowerCase();
    if (modelStr.includes('chatgpt') || modelStr.includes('gpt')) {
      const m = model.match(/gpt-?(\d+)[\.-]?(\d*)/i);
      if (m && m[1]) { const major = m[1]; const minor = m[2]; if (minor && parseFloat(minor) >= 5) return `ChatGPT ${major}.${minor}`; else if (major === '4' && minor === '0') return 'ChatGPT 4.0'; else if (parseFloat(major) >= 5) return `ChatGPT ${major}`; }
      return 'ChatGPT';
    }
    if (modelStr.includes('claude')) {
      const m = model.match(/claude-?(\d+)[-.]?(\d*)[-.]?(\w*)/i);
      if (m) { const major = m[1]; const minor = m[2]; const name = m[3] || 'sonnet'; if (minor && parseInt(minor) > 0) return `Claude ${major}.${minor} ${name.charAt(0).toUpperCase() + name.slice(1)}`; else return `Claude ${major} ${name.charAt(0).toUpperCase() + name.slice(1)}`; }
      return 'Claude';
    }
    if (modelStr.includes('gemini')) {
      const m = model.match(/gemini-?(\d+\.?\d*)[-.]?(\w*)/i);
      if (m) return `Gemini ${m[1]} ${(m[2] || 'pro').charAt(0).toUpperCase() + (m[2] || 'pro').slice(1)}`;
      return 'Gemini';
    }
    return model;
  };

  const getPlatformColor = (platform: string) => {
    const map: Record<string, string> = { chatgpt: isDark ? '#6ee7b7' : '#1a1a1a', claude: isDark ? '#fbbf24' : '#d97706', gemini: '#4285f4', 'browser_extension': '#6366f1' };
    return map[platform.toLowerCase()] || (isDark ? '#94a3b8' : '#1a1a1a');
  };

  const getAllFilesFromLog = (log: AuditLog): Array<{ name: string; source: 'attachment' | 'fileUpload' | 'sessionPrompt' }> => {
    const filesMap = new Map<string, { name: string; source: 'attachment' | 'fileUpload' | 'sessionPrompt' }>();
    if (log.sessionPrompts && Array.isArray(log.sessionPrompts)) {
      log.sessionPrompts.forEach(prompt => {
        if (prompt.files && Array.isArray(prompt.files)) {
          prompt.files.forEach(fileName => {
            if (fileName && typeof fileName === 'string') {
              const n = fileName.trim();
              if (n && n !== 'unknown' && !filesMap.has(n)) filesMap.set(n, { name: n, source: 'sessionPrompt' });
            }
          });
        }
      });
    }
    if (log.attachments && Array.isArray(log.attachments)) {
      log.attachments.forEach(att => {
        if (att.name) { const n = att.name.trim(); if (n && n !== 'unknown' && !filesMap.has(n)) filesMap.set(n, { name: n, source: 'attachment' }); }
      });
    }
    if (log.fileUploads && Array.isArray(log.fileUploads)) {
      log.fileUploads.forEach(fu => {
        if (fu.fileName) { const n = fu.fileName.trim(); if (n && n !== 'unknown' && !filesMap.has(n)) filesMap.set(n, { name: n, source: 'fileUpload' }); }
      });
    }
    return Array.from(filesMap.values());
  };

  const getFileIcon = (_fileName: string) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );

  const handleAuditRowClick = (log: AuditLog) => {
    setSelectedAudit(log);
    setExpandedFiles(new Set());
    setExpandedEncodedPrompt(false);
  };

  const handleCloseDetail = () => setSelectedAudit(null);

  const handleDownloadCSV = async (days: number) => {
    if (!userId || !auditData) return;
    setShowDownloadMenu(false);
    try {
      const auditLogs = await accountService.getUserAuditLogsForAdmin(userId, days);
      const csvHeaders = ['Timestamp','Platform','Protected From','Encoded Prompt','Risk Level','Entity Types','Tokens In','Tokens Out','Model','Files Attached','Peaq Transaction Hash','Continuation ID'];
      const csvRows = auditLogs.map((log: any) => {
        const entityTypes = Array.isArray(log.entityTypes) ? log.entityTypes.join('; ') : '';
        const files = Array.isArray(log.attachments) ? log.attachments.map((f: any) => `${f.name} (${f.type})`).join('; ') : '';
        return [log.timestamp || '',log.platform || '',clientDisplayName(log.client),`"${(log.encodedPrompt || '').replace(/"/g, '""')}"`,log.riskLevel || 'LOW',`"${entityTypes}"`,log.metadata?.tokensIn || log.tokensIn || 0,log.metadata?.tokensOut || log.tokensOut || 0,formatModelName(log.metadata?.model || log.model, log.platform),`"${files}"`,log.peaqTransactionHash || '',log.continuationId || ''];
      });
      const csvContent = [csvHeaders.join(','), ...csvRows.map((row: string[]) => row.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.setAttribute('href', URL.createObjectURL(blob));
      link.setAttribute('download', `user-audit-${auditData.user.firstName}-${auditData.user.lastName}-${days}days-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast(t`CSV report downloaded (${days} days)`, 'success');
    } catch (error: any) {
      console.error('Failed to download CSV:', error);
      showToast(t`Failed to download CSV report`, 'error');
    }
  };

  const handleAiSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!aiQuery.trim()) return;
    setAiResponse('');
    setTimeout(() => {
      if (!auditData) { setAiResponse(t`No user data available for analysis.`); return; }
      const totalPrompts = typeof auditData.stats.totalPrompts === 'number' ? auditData.stats.totalPrompts : 0;
      const platformCount = auditData.stats.platforms?.length || 0;
      const pdfCount = auditData.stats.filesShared?.pdf || 0;
      const csvCount = auditData.stats.filesShared?.csv || 0;
      const subject = auditData.user.firstName || t`this user`;
      const platformsLabel = plural(platformCount, { one: '# platform', other: '# platforms' });
      const summary = t`Based on the user's activity patterns, ${subject} shows ${totalPrompts} total prompts with activity across ${platformsLabel}.`;
      const fileNote = pdfCount > 0 || csvCount > 0
        ? t`Notably, ${pdfCount} PDF and ${csvCount} CSV files have been shared.`
        : t`No file attachments detected.`;
      setAiResponse(`${summary} ${fileNote}`);
    }, 1200);
  };

  if (loading) {
    return <Layout><div className="loading"><div className="spinner"></div></div></Layout>;
  }

  if (!auditData) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold mb-4"><Trans>User Not Found</Trans></h2>
          <button onClick={() => navigate('/users')} className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"><Trans>Back to User Management</Trans></button>
        </div>
      </Layout>
    );
  }

  const { user, usage } = auditData;
  const eventIdSuffix = selectedAudit?.id?.slice(-8) || t`N/A`;
  const detailTimestamp = selectedAudit ? formatFullTime(selectedAudit.timestamp) : '';
  const detailPromptCount = selectedAudit?.sessionPrompts?.length ?? 0;

  return (
    <Layout>
      <div style={{ fontFamily: "'Inter', sans-serif" }}>
        <button
          onClick={() => navigate('/users')}
          className="mb-4 text-sm flex items-center gap-2 hover:opacity-80 transition-opacity"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          <Trans>Back to User Management</Trans>
        </button>

        <div style={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: '1fr',
          gridTemplateRows: 'auto 1fr',
          gap: '2px',
          background: 'var(--color-border-subtle)',
          border: '1px solid var(--color-border-subtle)',
          boxShadow: '0 20px 50px var(--color-shadow)',
          borderRadius: '8px',
          overflow: 'hidden',
        }}>
          {/* Header Panel */}
          <header style={{
            gridColumn: '1',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-surface)',
            padding: '20px',
          }}>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{
                  width: '48px', height: '48px',
                  background: 'var(--color-surface-alt)',
                  borderRadius: '2px',
                  border: '1px solid var(--color-border-subtle)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 800, fontSize: '20px',
                  color: 'var(--color-text)',
                  flexShrink: 0,
                }}>{getInitials(user.firstName, user.lastName)}</div>
                <div>
                  <h1 className="text-xl sm:text-2xl md:text-3xl" style={{ fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1, color: 'var(--color-text)', margin: 0 }}>
                    {user.firstName} {user.lastName}
                  </h1>
                  <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: 'var(--color-text-secondary)', marginTop: '4px', textTransform: 'uppercase' }}>
                    {(user.effectiveStatus || user.status) !== 'pro' ? (
                      <Trans>Trial</Trans>
                    ) : user.effectiveTier === 'max' ? (
                      <span style={{ color: 'var(--color-max-text)', fontWeight: 700 }}>Max</span>
                    ) : (
                      'Pro'
                    )}
                    {' • '}{user.email}
                  </p>
                </div>
              </div>
              <div className="hidden sm:block" style={{ textAlign: 'right' }}>
                <span style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: 600, color: 'var(--color-text-secondary)', display: 'block', marginBottom: '4px' }}><Trans>Active window</Trans></span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'flex-end', alignItems: 'center' }}>
                  {[30, 90, 365].map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setAuditWindowDays(d)}
                      style={{
                        fontWeight: auditWindowDays === d ? 700 : 500,
                        fontSize: '12px',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        border: auditWindowDays === d ? '1px solid var(--color-text)' : '1px solid var(--color-border-subtle)',
                        background: auditWindowDays === d ? 'var(--color-surface-alt)' : 'transparent',
                        color: 'var(--color-text)',
                        cursor: 'pointer',
                        fontFamily: "'Inter', sans-serif",
                      }}
                    >
                      {d === 365 ? t`1 year` : t`${d}d`}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </header>

          {/* Main Content */}
          <main style={{
            display: 'flex', flexDirection: 'column', gap: '24px',
            background: 'var(--color-surface)',
            padding: '20px',
          }}>
            {/* AI Assistant */}
            <div style={{
              border: '1px solid var(--color-border-subtle)',
              background: 'var(--color-surface)',
              padding: '16px',
              boxShadow: '0 10px 30px var(--color-shadow)',
              width: '100%',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontWeight: 600, fontSize: '13px', color: 'var(--color-text)' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                </svg>
                <span><Trans>ANALYTIC AGENT</Trans></span>
              </div>
              <p style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginBottom: '6px' }}><Trans>Ask about this user's data patterns or security footprint.</Trans></p>
              <p style={{ fontSize: '10px', color: 'var(--color-text-muted)', marginBottom: '12px', fontStyle: 'italic' }}><Trans>Note: Analytic Agent is a placeholder. Real AI analysis coming soon.</Trans></p>
              <form onSubmit={handleAiSubmit} style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder={t`e.g. 'Summarize data exfiltration risks'`}
                  value={aiQuery}
                  onChange={(e) => setAiQuery(e.target.value)}
                  style={{
                    width: '100%', border: 'none',
                    borderBottom: `1.5px solid var(--color-text)`,
                    padding: '8px 0',
                    fontFamily: "'Inter', sans-serif", fontSize: '13px',
                    outline: 'none', background: 'transparent',
                    color: 'var(--color-text)',
                  }}
                />
              </form>
              {aiResponse && (
                <div style={{ marginTop: '15px', fontSize: '12px', lineHeight: 1.4, borderTop: '1px solid var(--color-border)', paddingTop: '10px', color: 'var(--color-text)' }}>
                  <span style={{ fontWeight: 600 }}><Trans>Insight:</Trans></span> {aiResponse}
                </div>
              )}
            </div>

            {/* Usage Chart - desktop */}
            <div className="hidden md:block" style={{ height: '200px', width: '100%', position: 'relative', marginBottom: '12px' }}>
              {usage.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={usage}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" opacity={0.5} />
                    <XAxis dataKey="date" stroke="var(--chart-axis)" style={{ fontSize: '10px' }} tick={{ fill: 'var(--chart-axis)', fontFamily: "'JetBrains Mono', monospace" }} />
                    <YAxis stroke="var(--chart-axis)" style={{ fontSize: '10px' }} tick={{ fill: 'var(--chart-axis)', fontFamily: "'JetBrains Mono', monospace" }} />
                    <Tooltip contentStyle={{ background: 'var(--tooltip-bg)', border: '1px solid var(--tooltip-border)', borderRadius: '0.5rem', fontFamily: "'Inter', sans-serif", color: 'var(--color-text)' }} />
                    {/* One line per provider that actually has activity in
                        the window — derived from the day-series keys, never
                        hardcoded, so new platforms (Grok, Codex→ChatGPT,
                        Hermes, 'other') appear without a portal change. */}
                    {USAGE_PROVIDER_SERIES.filter((s) => usage.some((d: Record<string, unknown>) => Number(d[s.key]) > 0)).map((s) => {
                      const stroke = s.key === 'chatgpt' ? (isDark ? '#6ee7b7' : '#111111') : s.color;
                      return (
                        <Line key={s.key} type="linear" dataKey={s.key} stroke={stroke} strokeWidth={2} dot={{ r: 3, fill: stroke, strokeWidth: 1, stroke }} activeDot={{ r: 5 }} name={s.label} />
                      );
                    })}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--color-text-muted)', fontSize: '14px' }}>
                  <Trans>No usage data available</Trans>
                </div>
              )}
            </div>

            {/* Mobile usage summary */}
            <div className="md:hidden grid grid-cols-2 gap-3">
              <div className="glass-panel rounded-lg p-3 text-center">
                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}><Trans>Total Prompts</Trans></p>
                <p className="text-lg font-bold">{auditData.stats.totalPrompts}</p>
              </div>
              <div className="glass-panel rounded-lg p-3 text-center">
                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}><Trans>Platforms</Trans></p>
                <p className="text-lg font-bold">{auditData.stats.platforms.length}</p>
              </div>
            </div>

            {/* Search + Download */}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', position: 'relative' }}>
              <input
                type="text"
                placeholder={t`Search prompts, files, or metadata...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1"
                style={{
                  padding: '12px 16px',
                  border: '1px solid var(--color-border-subtle)',
                  fontFamily: "'Inter', sans-serif", fontSize: '14px',
                  outline: 'none', transition: 'all 0.3s',
                  background: 'var(--color-surface)',
                  borderRadius: '4px',
                  color: 'var(--color-text)',
                }}
                onFocus={(e) => { e.target.style.borderColor = '#6366f1'; e.target.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = 'var(--color-border)'; e.target.style.boxShadow = 'none'; }}
              />
              <div style={{ position: 'relative' }}>
                <button onClick={() => setShowDownloadMenu(!showDownloadMenu)} className="p-2 transition-colors" style={{ color: 'var(--color-text-muted)' }} title={t`Download audit data as CSV`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                </button>
                {showDownloadMenu && (
                  <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '8px', background: 'var(--color-surface)', border: '1px solid var(--color-border-subtle)', borderRadius: '8px', boxShadow: '0 4px 12px var(--color-shadow)', zIndex: 1000, minWidth: '160px', padding: '8px' }} onMouseLeave={() => setShowDownloadMenu(false)}>
                    <div style={{ padding: '4px 8px', fontSize: '12px', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: '4px' }}><Trans>Select Time Range</Trans></div>
                    {[7, 30, 90, 365].map(days => (
                      <button key={days} onClick={() => handleDownloadCSV(days)} style={{ width: '100%', textAlign: 'left', padding: '8px 12px', fontSize: '14px', color: 'var(--color-text)', background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: '4px', fontFamily: "'Inter', sans-serif" }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-surface-hover)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >{days === 365 ? t`Past 1 year` : t`Past ${days} days`}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Audit List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: 'var(--color-border)' }}>
              {groupedLogs.length > 0 ? (
                groupedLogs.map((log, index) => {
                  const continuationId = log.continuationId || log.id;
                  const isExpanded = expandedRows.has(continuationId);
                  const hasMultiplePrompts = log.sessionPrompts && log.sessionPrompts.length > 1;
                  const allFiles = getAllFilesFromLog(log);
                  const agentNames = (log.agentExecutions || []).map(e => e.agentName).join(', ');
                  const uniqueFiles = Array.from(new Map(allFiles.map(f => [f.name, f])).values());
                  
                  return (
                    <React.Fragment key={log.id || index}>
                      <div
                        onClick={(e) => {
                          if (hasMultiplePrompts) {
                            e.stopPropagation();
                            const newExpanded = new Set(expandedRows);
                            if (isExpanded) newExpanded.delete(continuationId); else newExpanded.add(continuationId);
                            setExpandedRows(newExpanded);
                          } else {
                            handleAuditRowClick(log);
                          }
                        }}
                        style={{
                          background: 'var(--color-surface)',
                          padding: '16px',
                          display: 'grid',
                          gridTemplateColumns: hasMultiplePrompts ? '32px 100px 1fr auto 70px' : '100px 1fr auto 70px',
                          alignItems: 'center',
                          cursor: 'pointer',
                          transition: 'background 0.2s',
                          position: 'relative',
                          gap: '8px',
                        }}
                        className="hover-audit-row"
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-surface-hover)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-surface)'; }}
                      >
                        {hasMultiplePrompts && (
                          <span onClick={(e) => { e.stopPropagation(); const n = new Set(expandedRows); if (isExpanded) n.delete(continuationId); else n.add(continuationId); setExpandedRows(n); }}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '12px', color: 'var(--color-code-text)', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                            ▶
                          </span>
                        )}
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', padding: '4px 8px', border: `1px solid ${getPlatformColor(log.platform)}`, color: getPlatformColor(log.platform), width: 'fit-content', textTransform: 'uppercase' }}>
                          {getPlatformDisplayName(log.platform)}
                        </span>
                        {/* Which kind of device, beside which AI. Hidden on the
                            narrowest screens so the prompt preview keeps its
                            room; the detail pane always carries it. */}
                        <span className="hidden md:block" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', padding: '4px 8px', border: `1px solid ${surfaceColor(log.client, isDark)}`, color: surfaceColor(log.client, isDark), width: 'fit-content', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                          {clientDisplayName(log.client)}
                        </span>
                        <span className="hidden sm:block" style={{ fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingRight: '16px', color: 'var(--color-text)' }}>
                          {hasMultiplePrompts && log.sessionPrompts && log.sessionPrompts.length > 0
                            ? (log.sessionPrompts[0].encoded || log.sessionPrompts[0].original || t`No encoded prompt`)
                            : (log.encodedPrompt || log.prompt || t`No encoded prompt`)}
                        </span>
                        <span className="sm:hidden" style={{ fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--color-text)' }}>
                          {hasMultiplePrompts ? <Plural value={log.sessionPrompts?.length ?? 0} one="# prompt" other="# prompts" /> : (log.encodedPrompt || log.prompt || '').substring(0, 40)}
                        </span>
                        <span className="hidden sm:flex" style={{ textAlign: 'center', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                          {log.riskLevel === 'HIGH' && (
                            <span style={{ background: 'var(--color-risk-bg)', color: 'var(--color-risk-text)', padding: '2px 8px', fontSize: '10px', fontWeight: 700, borderRadius: '2px', textTransform: 'uppercase' }}><Trans>HIGH RISK</Trans></span>
                          )}
                          {log.agentExecutions && log.agentExecutions.length > 0 && (
                            <span style={{ background: 'rgba(139, 92, 246, 0.1)', color: 'rgb(139, 92, 246)', padding: '2px 8px', fontSize: '10px', fontWeight: 600, borderRadius: '2px', display: 'inline-flex', alignItems: 'center', gap: '3px' }} title={plural(log.agentExecutions.length, { one: `Agent: ${agentNames}`, other: `Agents: ${agentNames}` })}>
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" /></svg>
                              <Plural value={log.agentExecutions.length} one="# AGENT" other="# AGENTS" />
                            </span>
                          )}
                          {uniqueFiles.length > 0 && log.riskLevel !== 'HIGH' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                              {uniqueFiles.slice(0, 2).map((file, idx) => (
                                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '3px', padding: '2px 6px', background: 'var(--color-code-bg)', border: `1px solid var(--color-code-border)`, borderRadius: '4px', fontSize: '10px', color: 'var(--color-code-text)', fontFamily: "'JetBrains Mono', monospace", fontWeight: 500 }} title={file.name}>
                                  <span style={{ display: 'flex', alignItems: 'center', color: 'var(--color-code-text)' }}>{getFileIcon(file.name)}</span>
                                  <span style={{ maxWidth: '70px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                                </div>
                              ))}
                              {uniqueFiles.length > 2 && <span style={{ fontSize: '10px', color: 'var(--color-code-text)', fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, padding: '2px 6px', background: 'var(--color-code-bg)', border: `1px solid var(--color-code-border)`, borderRadius: '4px' }}>+{uniqueFiles.length - 2}</span>}
                            </div>
                          )}
                        </span>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: 'var(--color-text-secondary)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {formatTime(log.timestamp)}
                        </span>
                      </div>
                      {isExpanded && hasMultiplePrompts && log.sessionPrompts && (
                        <div style={{ background: 'var(--color-surface-secondary)', borderTop: '1px solid var(--color-border)', padding: '12px 16px 12px 48px' }}>
                          {log.sessionPrompts.map((prompt, promptIdx) => (
                            <div key={promptIdx} onClick={(e) => { e.stopPropagation(); handleAuditRowClick({ ...log, fullPrompt: prompt.original, encodedPrompt: prompt.encoded }); }}
                              style={{ padding: '12px', marginBottom: promptIdx < (log.sessionPrompts?.length || 0) - 1 ? '8px' : 0, background: 'var(--color-surface)', borderRadius: '4px', border: '1px solid var(--color-border)', cursor: 'pointer', transition: 'background 0.2s' }}
                              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-surface-hover)'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'var(--color-surface)'}
                            >
                              <div style={{ fontSize: '13px', color: 'var(--color-text)', marginBottom: '4px', fontWeight: 500 }}><Trans>Prompt {promptIdx + 1}</Trans></div>
                              <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {prompt.encoded || prompt.original || t`No encoded prompt`}
                              </div>
                              {prompt.files && prompt.files.length > 0 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '6px', flexWrap: 'wrap' }}>
                                  {prompt.files.slice(0, 2).map((fileName, fileIdx) => (
                                    <span key={fileIdx} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '2px 6px', background: 'var(--color-code-bg)', border: `1px solid var(--color-code-border)`, borderRadius: '4px', fontSize: '10px', color: 'var(--color-code-text)', fontFamily: "'JetBrains Mono', monospace" }}>
                                      {getFileIcon(fileName)} {fileName}
                                    </span>
                                  ))}
                                  {prompt.files.length > 2 && <span style={{ fontSize: '10px', color: 'var(--color-code-text)', fontFamily: "'JetBrains Mono', monospace" }}>+{prompt.files.length - 2}</span>}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </React.Fragment>
                  );
                })
              ) : (
                <div style={{ background: 'var(--color-surface)', padding: '40px', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                  {searchQuery ? <Trans>No audit logs match your search</Trans> : <Trans>No audit logs available</Trans>}
                </div>
              )}
            </div>
          </main>
        </div>

        {/* Detail Overlay */}
        {selectedAudit && (
          <div
            className="animate-slide-in"
            style={{
              position: 'fixed', top: 0, right: 0,
              width: '100%', maxWidth: '500px',
              height: '100vh',
              background: 'var(--color-surface)',
              zIndex: 100,
              boxShadow: '-20px 0 60px var(--color-shadow)',
              padding: '40px 24px',
              transform: 'translateX(0)',
              transition: 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
              borderLeft: '1px solid var(--color-border)',
              overflowY: 'auto',
            }}
          >
            <button onClick={handleCloseDetail} style={{ position: 'absolute', top: '20px', right: '20px', cursor: 'pointer', background: 'none', border: 'none', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text)' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>

            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', padding: '4px 8px', border: `1px solid ${getPlatformColor(selectedAudit.platform)}`, color: getPlatformColor(selectedAudit.platform), width: 'fit-content', textTransform: 'uppercase', marginBottom: '20px' }}>
              <Trans>Event Details #{eventIdSuffix}</Trans>
            </div>
            <h2 style={{ fontWeight: 800, fontSize: '22px', marginBottom: '8px', color: 'var(--color-text)' }}><Trans>Conversation Details</Trans></h2>
            <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: 'var(--color-text-secondary)', marginBottom: '32px' }}>
              <Trans>Timestamp: {detailTimestamp}</Trans>
              {selectedAudit.metadata?.promptCount && selectedAudit.metadata.promptCount > 1 && (
                <span style={{ marginLeft: '12px' }}>• <Plural value={selectedAudit.metadata.promptCount} one="# prompt" other="# prompts" /></span>
              )}
            </p>

            <div style={{ marginTop: '24px' }}>
              {selectedAudit.sessionPrompts && selectedAudit.sessionPrompts.length > 0 ? (
                <>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: 'var(--color-text-secondary)', marginBottom: '12px', display: 'block', borderBottom: '1px solid var(--color-border)', paddingBottom: '4px' }}>
                    <Trans>Conversation History ({detailPromptCount} prompts)</Trans>
                  </span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
                    {selectedAudit.sessionPrompts.map((prompt, promptIdx) => (
                      <div key={promptIdx} style={{ border: '1px solid var(--color-border)', borderRadius: '8px', overflow: 'hidden' }}>
                        <div style={{ background: 'var(--color-surface-secondary)', padding: '10px 16px', borderBottom: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase' }}><Trans>Prompt {promptIdx + 1}</Trans></span>
                          {prompt.timestamp && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', color: 'var(--color-text-muted)' }}>{formatTime(prompt.timestamp)}</span>}
                        </div>
                        <div style={{ padding: '16px' }}>
                          {/* Audit rows keep the protected prompt only; the API no longer returns originals. */}
                          {prompt.original && (
                            <>
                              <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', marginBottom: '8px', fontFamily: "'JetBrains Mono', monospace" }}><Trans>Original:</Trans></div>
                              <div style={{ background: 'var(--color-surface-secondary)', padding: '12px', border: '1px solid var(--color-border)', fontSize: '13px', lineHeight: 1.6, marginBottom: '12px', color: 'var(--color-text)', filter: 'blur(4px)', userSelect: 'none', cursor: 'not-allowed', borderRadius: '4px' }}>
                                {prompt.original}
                              </div>
                            </>
                          )}
                          {prompt.encoded && (
                            <>
                              <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', marginBottom: '8px', fontFamily: "'JetBrains Mono', monospace" }}><Trans>Privacy Twin (Encoded):</Trans></div>
                              <div style={{ background: 'var(--color-highlight-bg)', padding: '12px', border: '1px solid var(--color-highlight-border)', fontSize: '13px', lineHeight: 1.6, marginBottom: '12px', color: 'var(--color-text)', borderRadius: '4px' }}>
                                {prompt.encoded}
                              </div>
                            </>
                          )}
                          {prompt.files && prompt.files.length > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                              {prompt.files.map((fileName, fileIdx) => (
                                <span key={fileIdx} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', background: 'var(--color-code-bg)', border: `1px solid var(--color-code-border)`, borderRadius: '4px', fontSize: '10px', color: 'var(--color-code-text)', fontFamily: "'JetBrains Mono', monospace" }}>
                                  {getFileIcon(fileName)} {fileName}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  {/* Audit rows keep the protected prompt only; the API no longer returns originals. */}
                  {selectedAudit.fullPrompt && (
                    <>
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: 'var(--color-text-secondary)', marginBottom: '8px', display: 'block', borderBottom: '1px solid var(--color-border)', paddingBottom: '4px' }}><Trans>Original Prompt Text</Trans></span>
                      <div style={{ background: 'var(--color-surface-secondary)', padding: '15px', border: '1px solid var(--color-border)', fontSize: '14px', lineHeight: 1.6, marginBottom: '24px', color: 'var(--color-text)', filter: 'blur(4px)', userSelect: 'none', cursor: 'not-allowed' }}>
                        {(() => { const p = selectedAudit.fullPrompt || ''; return p.length > 512 ? p.substring(0, 512) + '...' : p; })()}
                      </div>
                    </>
                  )}
                  {selectedAudit.encodedPrompt && (
                    <>
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: 'var(--color-text-secondary)', marginBottom: '8px', display: 'block', borderBottom: '1px solid var(--color-border)', paddingBottom: '4px' }}><Trans>Privacy Twin (Encoded) Prompt</Trans></span>
                      <div style={{ background: 'var(--color-highlight-bg)', padding: '15px', border: '1px solid var(--color-highlight-border)', fontSize: '14px', lineHeight: 1.6, marginBottom: '24px', color: 'var(--color-text)' }}>
                        {(() => {
                          const ep = selectedAudit.encodedPrompt || '';
                          const shouldTruncate = ep.length > 512 && !expandedEncodedPrompt;
                          return (
                            <>
                              {shouldTruncate ? ep.substring(0, 512) + '...' : ep}
                              {ep.length > 512 && (
                                <button onClick={() => setExpandedEncodedPrompt(!expandedEncodedPrompt)}
                                  style={{ display: 'block', marginTop: '12px', padding: '6px 12px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = '#d97706'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = '#f59e0b'}
                                >{expandedEncodedPrompt ? <Trans>Show Less</Trans> : <Trans>Show More</Trans>}</button>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </>
                  )}
                </>
              )}

              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: 'var(--color-text-secondary)', marginBottom: '8px', display: 'block', borderBottom: '1px solid var(--color-border)', paddingBottom: '4px' }}><Trans>Metadata & Tokens</Trans></span>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" style={{ marginBottom: '24px' }}>
                <div style={{ padding: '12px', border: '1px solid var(--color-border)' }}>
                  <span style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: 600, color: 'var(--color-text-secondary)', display: 'block', marginBottom: '4px' }}><Trans>Platform</Trans></span>
                  <span style={{ fontSize: '13px', color: 'var(--color-text)' }}>{getPlatformDisplayName(selectedAudit.platform)}</span>
                </div>
                <div style={{ padding: '12px', border: '1px solid var(--color-border)' }}>
                  <span style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: 600, color: 'var(--color-text-secondary)', display: 'block', marginBottom: '4px' }}><Trans>Protected from</Trans></span>
                  <span style={{ fontSize: '13px', color: 'var(--color-text)' }}>{clientDisplayName(selectedAudit.client)}</span>
                </div>
                <div style={{ padding: '12px', border: '1px solid var(--color-border)' }}>
                  <span style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: 600, color: 'var(--color-text-secondary)', display: 'block', marginBottom: '4px' }}><Trans>Model</Trans></span>
                  <span style={{ fontSize: '13px', color: 'var(--color-text)' }}>{formatModelName(selectedAudit.metadata?.model || (selectedAudit as any).model, selectedAudit.platform)}</span>
                </div>
              </div>

              {(() => {
                const allFiles = getAllFilesFromLog(selectedAudit);
                const associatedFileCount = allFiles.length;
                if (allFiles.length > 0) {
                  return (
                    <>
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: 'var(--color-text-secondary)', marginBottom: '8px', display: 'block', borderBottom: '1px solid var(--color-border)', paddingBottom: '4px', marginTop: '24px' }}><Trans>Associated Files ({associatedFileCount})</Trans></span>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' }}>
                        {allFiles.map((file, idx) => (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: 'var(--color-surface-secondary)', border: '1px solid var(--color-border)', borderRadius: '6px', transition: 'all 0.2s' }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-surface-hover)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-surface-secondary)'; }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', color: 'var(--color-code-text)', flexShrink: 0 }}>{getFileIcon(file.name)}</div>
                            <span style={{ flex: 1, fontSize: '13px', color: 'var(--color-text)', fontWeight: 500, fontFamily: "'Inter', sans-serif", overflow: 'hidden', textOverflow: 'ellipsis' }}>{file.name}</span>
                            <span style={{ fontSize: '10px', color: 'var(--color-text-secondary)', fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase' }}>{file.name.split('.').pop() || t`FILE`}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                }
                return null;
              })()}

              {selectedAudit.peaqTransactionHash && (
                <>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: 'var(--color-text-secondary)', marginBottom: '8px', display: 'block', borderBottom: '1px solid var(--color-border)', paddingBottom: '4px', marginTop: '24px' }}><Trans>Onchain Audit Transaction</Trans></span>
                  <div style={{ padding: '12px', background: 'var(--color-info-bg)', border: '1px solid var(--color-info-border)', borderRadius: '4px', marginBottom: '24px' }}>
                    <div style={{ marginBottom: '8px', fontSize: '12px', color: 'var(--color-text)', wordBreak: 'break-all' }}>
                      <strong><Trans>Transaction Hash:</Trans></strong> {selectedAudit.peaqTransactionHash}
                    </div>
                    <a href={`https://peaq.subscan.io/tx/${selectedAudit.peaqTransactionHash}`} target="_blank" rel="noopener noreferrer"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#0ea5e9', textDecoration: 'none', fontSize: '12px', fontWeight: 600 }}
                      onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                      onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" /></svg>
                      <Trans>View on Peaq Subscan</Trans>
                    </a>
                  </div>
                </>
              )}

              {selectedAudit.entityTypes && selectedAudit.entityTypes.length > 0 && (
                <>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: 'var(--color-text-secondary)', marginBottom: '8px', display: 'block', borderBottom: '1px solid var(--color-border)', paddingBottom: '4px', marginTop: '24px' }}><Trans>Detected Entity Types</Trans></span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '24px' }}>
                    {Array.from(new Set(selectedAudit.entityTypes)).map((entityType, idx) => (
                      <span key={idx} style={{ padding: '4px 8px', background: 'var(--color-entity-bg)', border: '1px solid var(--color-entity-border)', borderRadius: '4px', fontSize: '10px', fontFamily: "'JetBrains Mono', monospace", color: 'var(--color-entity-text)', fontWeight: 600 }}>
                        {entityType}
                      </span>
                    ))}
                  </div>
                </>
              )}

              {selectedAudit.attachments && selectedAudit.attachments.length > 0 && (
                <>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: 'var(--color-text-secondary)', marginBottom: '8px', display: 'block', borderBottom: '1px solid var(--color-border)', paddingBottom: '4px', marginTop: '24px' }}><Trans>Attached Files</Trans></span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '24px' }}>
                    {selectedAudit.attachments.map((file, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', background: 'var(--color-entity-bg)', border: '1px solid var(--color-border)', borderRadius: '6px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-secondary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '12px', color: 'var(--color-text-primary)' }}>{file.name}</span>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', color: 'var(--color-text-secondary)', marginLeft: 'auto', textTransform: 'uppercase' }}>{file.type}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {selectedAudit && (
          <div onClick={handleCloseDetail} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'var(--color-overlay)', zIndex: 99 }} />
        )}
      </div>
    </Layout>
  );
}
